import { Logo } from '@/components/atoms/Logo';
import type { BreadcrumbItem } from '@/components/organisms/WebHeader';
import { getUnauthenticatedLandingPath } from '@/core/constants/appConfig';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackGuestLanguageChange } from '@/core/services/trackDynatraceCatalog';
import { LanguageSwitch } from '@/modules/profile/components/LanguageSwitch';
import { router } from 'expo-router';
import React, { useCallback } from 'react';
import { Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import {
  GUEST_PHONE_MAX,
  resolveGuestChromePaddingHorizontal,
} from '../constants/guestLayout';
import { GuestBreadcrumbHeader } from './GuestBreadcrumbHeader';

export interface GuestTopBarProps {
  readonly headerTitle?: string;
  readonly breadcrumbs?: ReadonlyArray<BreadcrumbItem>;
  readonly showBackButton?: boolean;
  readonly onBackPress?: () => void;
  readonly projectId?: string;
}

/**
 * Guest top chrome: logo + optional back/title/breadcrumbs + language pill.
 * On phone unit-details (nav present), hide the logo like WebHeader mobile.
 * Logo → Bloom Universal hub or Welcome per `ENABLE_BLOOM_UNIVERSAL_LANDING`.
 */
export function GuestTopBar({
  headerTitle,
  breadcrumbs,
  showBackButton = false,
  onBackPress,
  projectId,
}: Readonly<GuestTopBarProps>) {
  const colors = useUIColors();
  const { currentLanguage, changeLanguage } = useTranslation();
  const { width } = useWindowDimensions();
  const isPhone = width < GUEST_PHONE_MAX;
  const paddingHorizontal = resolveGuestChromePaddingHorizontal(width);
  const hasGuestNav =
    showBackButton || (breadcrumbs != null && breadcrumbs.length > 0);
  const showLogo = !isPhone || !hasGuestNav;
  const backPressProps = onBackPress ? { onBackPress } : {};

  const handleLanguageChange = useCallback(
    (value: 'en' | 'ar') => {
      if (value === currentLanguage) {
        return;
      }
      trackGuestLanguageChange(value, projectId);
      void changeLanguage(value);
    },
    [changeLanguage, currentLanguage, projectId],
  );

  const handleLogoPress = useCallback(() => {
    router.replace(getUnauthenticatedLandingPath() as never);
  }, []);

  const navBlock =
    hasGuestNav && headerTitle != null ? (
      <GuestBreadcrumbHeader
        title={headerTitle}
        breadcrumbs={breadcrumbs ?? []}
        showBackButton={showBackButton}
        {...backPressProps}
      />
    ) : null;

  return (
    <View
      style={[
        styles.wrap,
        {
          backgroundColor: colors.backgroundLevel2,
          paddingHorizontal,
        },
      ]}
    >
      <View style={styles.row}>
        <View style={styles.leading}>
          {showLogo ? (
            <Pressable
              onPress={handleLogoPress}
              accessibilityRole="link"
              accessibilityLabel="Bloom"
              style={({ pressed }) => [
                styles.logoSlot,
                pressed ? styles.logoPressed : null,
              ]}
            >
              <Logo color="bloomRed" size="large" />
            </Pressable>
          ) : null}
          {navBlock}
        </View>
        <LanguageSwitch
          value={currentLanguage}
          onChange={handleLanguageChange}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    width: '100%',
    paddingTop: 12,
    paddingBottom: 8,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    minHeight: 48,
    gap: 8,
  },
  leading: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  logoSlot: {
    width: 128,
    height: 48,
    alignItems: 'flex-start',
    justifyContent: 'center',
    flexShrink: 0,
  },
  logoPressed: {
    opacity: 0.85,
  },
});
